import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import RoleBasedHeader from '../components/ui/RoleBasedHeader';
import Icon from '../components/AppIcon';

// Official CBIC GST state code list — stable since GST rollout, used to
// translate the 2-digit code from GSTIN verification into a readable name.
const GST_STATE_CODES = {
  '01': 'Jammu and Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab',
  '04': 'Chandigarh', '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi',
  '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
  '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram',
  '16': 'Tripura', '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal',
  '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh',
  '24': 'Gujarat', '25': 'Dadra and Nagar Haveli and Daman and Diu',
  '26': 'Dadra and Nagar Haveli and Daman and Diu', '27': 'Maharashtra',
  '28': 'Andhra Pradesh (Old)', '29': 'Karnataka', '30': 'Goa',
  '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu', '34': 'Puducherry',
  '35': 'Andaman and Nicobar Islands', '36': 'Telangana', '37': 'Andhra Pradesh',
  '38': 'Ladakh', '97': 'Other Territory', '99': 'Centre Jurisdiction',
};

// Roles that require a GSTIN — matches user-registration/index.jsx exactly.
// Accounts outside this list (sales_agent, transporter) don't get a
// self-service GSTIN option here; that path goes through the separate
// verified upgrade flow.
const GSTIN_ROLES = ['buyer', 'seller', 'factory'];
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[A-Z]{1}[0-9A-Z]{1}$/;

const Toast = ({ message, type, onClose }) => (
  <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm font-medium transition-all duration-300 ${type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
    <Icon name={type === 'success' ? 'CheckCircle' : 'XCircle'} size={18} />
    <span>{message}</span>
    <button onClick={onClose} className="ml-2 hover:opacity-80"><Icon name="X" size={16} /></button>
  </div>
);

const Profile = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [formData, setFormData] = useState({
    full_name: '',
    mobile_phone: '',
    whatsapp_phone: '',
    company_name: '',
    gstin: '',
    pan: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: '',
  });
  const [errors, setErrors] = useState({});
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // GSTIN add/verify — kept separate from the main form/save flow since it
  // involves an external verification call, not a plain field edit.
  const [userRole, setUserRole] = useState('');
  const [gstinLocked, setGstinLocked] = useState(true); // true once a GSTIN exists — permanent, no exceptions
  const [gstinEditing, setGstinEditing] = useState(false);
  const [gstinInput, setGstinInput] = useState('');
  const [gstinVerifying, setGstinVerifying] = useState(false);
  const [gstinVerifyError, setGstinVerifyError] = useState('');

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login-authentication');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    const fetchProfile = async () => {
      setIsFetching(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('user_id', user?.id)?.single();
        if (!error && data) {
          setFormData({
            full_name: data?.full_name || '',
            mobile_phone: data?.mobile_phone || '',
            whatsapp_phone: data?.whatsapp_phone || '',
            company_name: data?.company_name || '',
            gstin: data?.gstin || '',
            pan: data?.pan || '',
            address_line1: data?.address_line1 || '',
            address_line2: data?.address_line2 || '',
            city: data?.city || '',
            state: data?.state || '',
            pincode: data?.pincode || '',
          });
          setUserRole(data?.role || '');
          // Locked the moment a GSTIN exists on the profile as fetched —
          // this is the one-time snapshot that decides lock state, not
          // whatever formData.gstin becomes during editing.
          setGstinLocked(!!data?.gstin);
        }
      } catch (err) {
        console.error('Error fetching profile:', err);
      } finally {
        setIsFetching(false);
      }
    };
    fetchProfile();
  }, [user]);

  const showToast = (message, type) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData?.full_name?.trim()) newErrors.full_name = 'Full name is required';
    if (!formData?.mobile_phone?.trim()) newErrors.mobile_phone = 'Mobile phone is required';
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSaving(true);
    try {
      const { gstin: _gstin, ...updatePayload } = formData;
      const { error } = await supabase?.from('user_profiles')?.update(updatePayload)?.eq('user_id', user?.id);
      if (error) throw error;
      showToast('Profile updated successfully!', 'success');
    } catch (err) {
      showToast(err?.message || 'Failed to update profile. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddGstin = async () => {
    const gstin = gstinInput.trim().toUpperCase();
    if (!GSTIN_REGEX.test(gstin)) {
      setGstinVerifyError('Invalid GSTIN format. Must be 15 characters (e.g., 27AABCU9603R1ZX)');
      return;
    }
    setGstinVerifying(true);
    setGstinVerifyError('');
    try {
      const { data, error } = await supabase.functions.invoke('verify-gstin', { body: { gstin } });
      if (error || !data?.success) {
        setGstinVerifyError(data?.error || error?.message || 'Could not verify GSTIN. Please retry.');
        return;
      }
      // Autofill from GST verification data — mirrors what pwa-user-signup
      // already does at registration time, applied here for existing users
      // adding/verifying GSTIN later. Only fills fields that are currently
      // empty; never overwrites anything the user already typed.
      // business_type is no longer a field on this page at all (removed —
      // role already captures this; GST's taxpayer_type never mapped to it
      // correctly anyway). state now correctly maps GST's 2-digit state_code
      // through GST_STATE_CODES to a readable name, not the raw digits.
      const autofill = {};
      if (!formData?.company_name?.trim() && data?.legal_name) autofill.company_name = data.legal_name;
      if (!formData?.address_line1?.trim() && data?.address) autofill.address_line1 = data.address;
      if (!formData?.pincode?.trim() && data?.pincode) autofill.pincode = data.pincode;
      if (!formData?.state?.trim() && data?.state_code && GST_STATE_CODES[data.state_code]) autofill.state = GST_STATE_CODES[data.state_code];

      const { error: saveError } = await supabase?.from('user_profiles')?.update({ gstin, ...autofill })?.eq('user_id', user?.id);
      if (saveError) throw saveError;

      setFormData(prev => ({ ...prev, gstin, ...autofill }));
      setGstinLocked(true);
      setGstinEditing(false);
      setGstinInput('');
      showToast('GSTIN verified and saved.', 'success');
    } catch (err) {
      if (err?.code === '23505' || err?.message?.includes('user_profiles_gstin_unique')) {
        setGstinVerifyError('This GSTIN is already registered to another SugarHathi account. If this is a mistake, please contact support.');
      } else {
        setGstinVerifyError(err?.message || 'Failed to save GSTIN. Please try again.');
      }
    } finally {
      setGstinVerifying(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    try {
      const { error } = await supabase.functions.invoke('deactivate-account');
      if (error) throw error;
      await supabase.auth.signOut();
      navigate('/login-authentication');
    } catch (err) {
      showToast(err?.message || 'Failed to delete account. Please try again.', 'error');
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-colors ${errors?.[field] ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`;

  if (loading || isFetching) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-[#1e4a72] border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RoleBasedHeader />
      {toast && <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Page Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#1e4a72]">My Profile</h1>
            <p className="text-sm text-gray-500 mt-0.5">Manage your personal and business information</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Personal Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                <Icon name="User" size={16} className="text-[#1e4a72]" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Personal Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="full_name"
                  value={formData?.full_name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  className={inputClass('full_name')}
                />
                {errors?.full_name && <p className="text-xs text-red-500 mt-1">{errors?.full_name}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Mobile Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobile_phone"
                  value={formData?.mobile_phone}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  className={inputClass('mobile_phone')}
                />
                {errors?.mobile_phone && <p className="text-xs text-red-500 mt-1">{errors?.mobile_phone}</p>}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">WhatsApp Phone</label>
                <input
                  type="tel"
                  name="whatsapp_phone"
                  value={formData?.whatsapp_phone}
                  onChange={handleChange}
                  placeholder="Enter WhatsApp number"
                  className={inputClass('whatsapp_phone')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="text"
                  value={user?.email || 'Not available'}
                  disabled
                  readOnly
                  className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
                />
              </div>
            </div>
          </div>

          {/* Business Information */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-5">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                <Icon name="Building2" size={16} className="text-[#ff8c00]" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Business Information</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Company Name</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData?.company_name}
                  onChange={handleChange}
                  placeholder="Enter company name"
                  className={inputClass('company_name')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">GSTIN</label>
                {gstinLocked ? (
                  <input
                    type="text"
                    name="gstin"
                    value={formData?.gstin || 'Not provided'}
                    disabled
                    readOnly
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
                  />
                ) : !GSTIN_ROLES.includes(userRole) ? (
                  <input
                    type="text"
                    value="Not provided"
                    disabled
                    readOnly
                    className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
                  />
                ) : !gstinEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value="Not provided"
                      disabled
                      readOnly
                      className="flex-1 px-3 py-2 text-sm border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed border-gray-300"
                    />
                    <button
                      type="button"
                      onClick={() => { setGstinEditing(true); setGstinVerifyError(''); }}
                      className="px-3 py-2 text-sm font-medium rounded-lg border border-[#1e4a72] text-[#1e4a72] hover:bg-blue-50 whitespace-nowrap"
                    >
                      Add GSTIN
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={gstinInput}
                        onChange={(e) => { setGstinInput(e.target.value.toUpperCase()); setGstinVerifyError(''); }}
                        placeholder="29ABCDE1234F1Z5"
                        maxLength={15}
                        disabled={gstinVerifying}
                        className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e4a72] ${gstinVerifyError ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`}
                      />
                      <button
                        type="button"
                        onClick={handleAddGstin}
                        disabled={gstinVerifying || !gstinInput.trim()}
                        className="px-3 py-2 text-sm font-medium rounded-lg bg-[#1e4a72] text-white disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      >
                        {gstinVerifying ? 'Verifying...' : 'Verify & Save'}
                      </button>
                      <button
                        type="button"
                        onClick={() => { setGstinEditing(false); setGstinInput(''); setGstinVerifyError(''); }}
                        disabled={gstinVerifying}
                        className="px-2 py-2 text-sm text-gray-500 hover:text-gray-700"
                      >
                        Cancel
                      </button>
                    </div>
                    {gstinVerifyError && (
                      <p className="text-xs text-red-600 mt-1">{gstinVerifyError}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-1">
                      This can only be set once — it cannot be changed after saving.
                    </p>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">PAN</label>
                <input
                  type="text"
                  name="pan"
                  value={formData?.pan}
                  onChange={handleChange}
                  placeholder="Enter PAN"
                  className={inputClass('pan')}
                />
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
            <div className="flex items-center gap-2 mb-5">
              <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
                <Icon name="MapPin" size={16} className="text-green-600" />
              </div>
              <h2 className="text-base font-semibold text-gray-800">Address</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Address Line 1</label>
                <input
                  type="text"
                  name="address_line1"
                  value={formData?.address_line1}
                  onChange={handleChange}
                  placeholder="Street address, building name"
                  className={inputClass('address_line1')}
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Address Line 2</label>
                <input
                  type="text"
                  name="address_line2"
                  value={formData?.address_line2}
                  onChange={handleChange}
                  placeholder="Area, locality (optional)"
                  className={inputClass('address_line2')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">City</label>
                <input
                  type="text"
                  name="city"
                  value={formData?.city}
                  onChange={handleChange}
                  placeholder="City"
                  className={inputClass('city')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">State</label>
                <input
                  type="text"
                  name="state"
                  value={formData?.state}
                  onChange={handleChange}
                  placeholder="State"
                  className={inputClass('state')}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">PIN Code</label>
                <input
                  type="text"
                  name="pincode"
                  value={formData?.pincode}
                  onChange={handleChange}
                  placeholder="PIN Code"
                  className={inputClass('pincode')}
                />
              </div>
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSaving}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] hover:opacity-90 rounded-xl transition-all duration-200 shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Icon name="Save" size={16} />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </form>

        {/* Danger Zone */}
        <div className="mt-8 bg-white rounded-2xl shadow-sm border border-red-100 p-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-red-100 flex items-center justify-center">
              <Icon name="AlertTriangle" size={16} className="text-red-600" />
            </div>
            <h2 className="text-base font-semibold text-gray-800">Danger Zone</h2>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Deleting your account will block your login immediately. You can reactivate anytime if you decide to come back.
          </p>
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors duration-200"
          >
            <Icon name="Trash2" size={16} />
            <span>Delete My Account</span>
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Icon name="AlertTriangle" size={20} className="text-red-600" />
              </div>
              <h3 className="text-base font-bold text-gray-900">Are you sure?</h3>
            </div>
            <p className="text-sm text-gray-500 mb-6">
              This will deactivate your account and log you out immediately. You can reactivate anytime to come back.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteAccount}
                disabled={isDeleting}
                className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors disabled:opacity-60"
              >
                {isDeleting ? 'Deleting...' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
